import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Container, Stack, Group, Title, Text, Badge, Drawer, Tabs, Card, Grid, Alert, Button, ActionIcon, Tooltip } from '@mantine/core';
import { IconChartBar, IconClipboardList, IconTarget, IconRadar, IconShield, IconAlertCircle, IconAlertTriangle, IconSend, IconCheck, IconX, IconRobot, IconDoorExit, IconPlayerPlay } from '@tabler/icons-react';
import SectionAssessmentCardV2 from './SectionAssessmentCardV2';
import ControlEvaluationWizard from '../riskFlow/ControlEvaluationWizard';
import RiskFlowWrapperV2 from '../riskFlow/RiskFlowWrapperV2';
import ChartsSummaryV2 from './ChartsSummaryV2';
import ActionPlan from '../legacy/ActionPlan';
import Monitors from '../Monitors';
import RiskManagerToolkit from '../RiskManagerToolkit';
import SecurityThreatsCardV2 from './SecurityThreatsCardV2';
import AIScoreExplanationModal from './components/AIScoreExplanationModal';
import { calculateRiskSeverityScore, calculateMitigationQualityScore, calculateSecurityThreatLevel } from '../utils/aiScoreCalculators';
import { isRiskManager } from '../utils/roleHelpers';
// RTK async thunks for assessment operations
import { fetchRiskCardData, initializeAssessmentLocal, fetchSingleAssessment, selectReviewSessionInfo } from '../redux/slices/assessmentsSlice';
import { fetchAssessmentRisks } from '../redux/slices/risksSlice';
import { setSavedControls, fetchSavedControlEvaluations } from '../redux/actions/controlActions';
import { fetchResidualRisk } from '../redux/actions/residualRiskActions';
import { fetchSecurityRisks, fetchScenarios } from '../redux/slices/securitySlice';
import { submitForReview } from '../redux/actions/reviewActions';
import { startReview } from '../redux/slices/reviewSlice';
import ReviewMode from '../review/ReviewMode';
import { jwtDecode } from 'jwt-decode';
import { createRiskFormSelector } from '../redux/selectors/riskFormGenerator';
import { selectRisksStateBridge, selectSecurityBridge, selectSavedControlsBridge } from '../redux/selectors/assessmentSelectors';

function useInitialRef(value) {
  const ref = useRef(value);
  // lock the initial value; do not update on changes
  return ref;
}

export default function AssessmentCanvasV2({ modelId, assessmentUuid: propAssessmentUuid, assessmentName: propAssessmentName }) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [userIsRiskManager, setUserIsRiskManager] = useState(false);

  // All hooks must be called before any conditional returns
  // RTK-first: Get assessment data from RTK assessments slice
  const assessmentsById = useSelector((state) => state.assessments?.byId || {});
  const modelsById = useSelector((state) => state.models?.byId || {});

  // Find assessment by assessmentUuid prop or by modelId
  const rtkAssessment = useMemo(() => {
    if (propAssessmentUuid && assessmentsById[propAssessmentUuid]) {
      return assessmentsById[propAssessmentUuid];
    }
    // Fallback: find by model_uuid
    return Object.values(assessmentsById).find(a => a.model_uuid === modelId);
  }, [assessmentsById, propAssessmentUuid, modelId]);

  const rtkAssessmentUuid = rtkAssessment?.info?.assessment_uuid || rtkAssessment?.assessment_uuid;

  // Get assessmentUuid: props first, then RTK slice, then legacy Redux
  const assessmentUuid = propAssessmentUuid || rtkAssessmentUuid || null;

  // Get user roles from Auth0 token and check if user is Risk Manager
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            scope: 'openid profile email read:models write:models delete:models'
          }
        });

        const decoded = jwtDecode(token);
        const roles = decoded['https://agenthuginn.ai/claims/roles'] || decoded['https://riskgpt.io/claims/roles'] || [];
        const isRM = isRiskManager(roles);
        setUserIsRiskManager(isRM);
        console.log('[AssessmentCanvasV2] User roles:', roles, 'Is Risk Manager:', isRM);
      } catch (error) {
        console.error('[AssessmentCanvasV2] Error fetching roles:', error);
        setUserIsRiskManager(false);
      }
    };

    if (isAuthenticated) {
      fetchRoles();
    }
  }, [getAccessTokenSilently, isAuthenticated]);
  // RTK-first: Use bridge selector for security state (keys by modelId, falls back to assessmentUuid)
  const securityState = useSelector(selectSecurityBridge(assessmentUuid, modelId));
  const assessmentEntry = useSelector((state) => assessmentUuid ? state.assessments?.byId?.[assessmentUuid] : null);
  const currentUser = useSelector((state) => state.app?.currentUser);
  // RTK-first: read from RTK slice, fall back to legacy
  const residualRiskData = useSelector((state) => {
    const rtkData = state.residualRiskRTK?.byAssessment?.[assessmentUuid] || {};
    const legacyData = state.residualRisk?.byModel?.[assessmentUuid] || {};
    return { ...legacyData, ...rtkData };
  });
  // Use assessment-scoped selector for saved controls (filters by assessmentUuid)
  const savedControls = useSelector(selectSavedControlsBridge(assessmentUuid, modelId));
  const contextData = useSelector((state) => state.context.data[modelId] || {});

  // RTK: Get template-based modelRisks for initializing assessment if needed
  const riskFormSelector = useMemo(
    () => assessmentUuid ? createRiskFormSelector(assessmentUuid) : () => ({}),
    [assessmentUuid]
  );
  const templateModelRisks = useSelector(riskFormSelector);

  // RTK-first: Use bridge selector to read risks from riskItems slice (where fetchAssessmentRisks populates data)
  const risksState = useSelector(selectRisksStateBridge(assessmentUuid, modelId));
  const riskFormData = useMemo(
    () => risksState.modelRisks || risksState.riskFormData || templateModelRisks || {},
    [risksState.modelRisks, risksState.riskFormData, templateModelRisks]
  );

  // Get review status from assessment data (primary source)
  // Legacy fallback for backward compatibility during transition
  const legacyReviewStatus = useSelector((state) => state.reviews.byModel[modelId]?.reviewStatus);
  const reviewStatus = assessmentEntry?.review_status || legacyReviewStatus || null;
  const reviewLoading = useSelector((state) => state.reviews.loading);

  const originalRiskFormDataRef = useInitialRef(riskFormData);

  const areas = useMemo(() => Object.keys(riskFormData || {}), [riskFormData]);

  // Prepare narratives for Risk Manager Toolkit
  const narratives = useMemo(() => {
    return Object.keys(contextData).reduce((acc, section) => {
      const narrative = contextData[section]?.narrative
        ? contextData[section].narrative
        : `No narrative for ${section}`;
      acc[section] = narrative;
      return acc;
    }, {});
  }, [contextData]);

  // Enhanced state for card expansion - only one can be expanded at a time
  const [expandedArea, setExpandedArea] = useState(null);

  // Drawer for evaluating actions on a specific risk
  const [evalDrawer, setEvalDrawer] = useState({ opened: false, riskArea: null, source: null });

  // Drawer for editing risk sources and scenarios
  const [editDrawer, setEditDrawer] = useState({ opened: false, riskArea: null });

  // Modal for AI score explanations
  const [explanationModal, setExplanationModal] = useState({ opened: false, scoreType: null, scoreData: null });

  // Review mode state
  const [isReviewModeActive, setIsReviewModeActive] = useState(false);

  // Fetch all assessment data on mount
  useEffect(() => {
    // Guard: Don't fetch if assessmentUuid or modelId is missing
    if (!assessmentUuid || !modelId) {
      console.log('[AssessmentCanvasV2] Skipping fetch - missing assessmentUuid or modelId', { modelId, assessmentUuid });
      return;
    }

    console.log('[AssessmentCanvasV2] Fetching all assessment data on mount...', { modelId, assessmentUuid });

    const fetchAllData = async () => {
      try {
        // CRITICAL: Initialize assessment in state if not present
        // This is required for fetchRiskCardData to work (it checks state.risks.byAssessment[assessmentUuid])
        const existingRiskFormData = assessmentEntry?.riskFormData || {};
        if (Object.keys(existingRiskFormData).length === 0 && Object.keys(templateModelRisks).length > 0) {
          const initModelName = rtkAssessment?.model_name || 'Unknown Model';
          console.log('[AssessmentCanvasV2] Initializing assessment in state with template modelRisks...', { assessmentUuid, modelId, initModelName });
          dispatch(initializeAssessmentLocal({
            assessmentUuid,
            modelId,
            modelName: initModelName,
            assessmentName: rtkAssessment?.assessment_name || initModelName,
            modelType: rtkAssessment?.model_type || '',
            modelStage: rtkAssessment?.model_stage || '',
            useCaseShortDescription: rtkAssessment?.use_case_short_description || '',
            // CRITICAL: Don't override status with fallback - preserve existing status from DB
            // Only include assessmentStatus if it has a real value
            ...(rtkAssessment?.info?.status && { assessmentStatus: rtkAssessment.info.status }),
            currentStep: rtkAssessment?.current_step || '',
            modelRisks: templateModelRisks,
            templateId: rtkAssessment?.info?.template_id || '',
            mode: rtkAssessment?.info?.mode || 'autopilot',
            autonomous: rtkAssessment?.info?.autonomous || false,
            position: rtkAssessment?.position || {},
          }));
        }

        // Fetch risk card data (risk sources, scenarios, priorities)
        await dispatch(fetchRiskCardData({ assessmentUuid, modelId }));
        console.log('[AssessmentCanvasV2] ✓ Fetched risk card data');

        // CRITICAL: Fetch saved risks from risks table
        // This populates modelRisks.areaRisks with risks saved during autopilot
        await dispatch(fetchAssessmentRisks({ assessmentUuid, modelId }));
        console.log('[AssessmentCanvasV2] ✓ Fetched assessment risks');

        // Fetch risk evaluations (if any area-level evaluations exist)
        // Note: fetchRiskEvaluations requires riskArea parameter, so we'll skip it here
        // as it's typically called per-area when needed

        // Fetch saved controls (mitigation actions)
        await dispatch(setSavedControls(assessmentUuid, modelId));
        console.log('[AssessmentCanvasV2] ✓ Fetched saved controls');

        // Fetch control evaluations
        await dispatch(fetchSavedControlEvaluations(assessmentUuid, modelId));
        console.log('[AssessmentCanvasV2] ✓ Fetched control evaluations');

        // Fetch residual risk assessments - assessment-centric: (assessmentUuid, modelId)
        await dispatch(fetchResidualRisk(assessmentUuid, modelId));
        console.log('[AssessmentCanvasV2] ✓ Fetched residual risks');

        // Fetch security scenarios/threats
        await dispatch(fetchSecurityRisks(modelId, assessmentUuid, false));
        console.log('[AssessmentCanvasV2] ✓ Fetched security risks');

        // Fetch security scenarios - RTK thunk requires object parameter
        await dispatch(fetchScenarios({ modelId, assessmentUuid }));
        console.log('[AssessmentCanvasV2] ✓ Fetched security scenarios');

        // Note: Security controls will be fetched on-demand by SecurityTabPanel when scenarios are viewed

        console.log('[AssessmentCanvasV2] All data fetched successfully');
      } catch (error) {
        console.error('[AssessmentCanvasV2] Error fetching assessment data:', error);
      }
    };

    fetchAllData();
    // Note: Dependencies intentionally exclude rtkAssessment, legacyRiskFormData and templateRiskFormData
    // to avoid refetch loops. rtkAssessment changes when dispatches update state, causing infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, assessmentUuid, dispatch]);

  // Get model name and assessment name - RTK first, then legacy
  const modelName = rtkAssessment?.model_name
    || modelsById[modelId]?.model_name
    || securityState?.modelName
    || 'Unknown Model';

  const assessmentName = propAssessmentName
    || rtkAssessment?.info?.assessment_name
    || null;
  const assessmentStatus = rtkAssessment?.info?.status || assessmentEntry?.info?.status || null;
  const assessmentType = rtkAssessment?.assessment_type || assessmentEntry?.assessment_type || 'self-assessment';

  // Get review session info for displaying reviewer metadata
  const reviewSessionInfo = useSelector((state) =>
    selectReviewSessionInfo(state, assessmentUuid)
  );

  // Calculate AI-powered scores
  const aiScores = useMemo(() => {
    // Calculate risk severity from priority counts
    const priorityCounts = {
      Critical: 0,
      High: 0,
      Significant: 0,
      Moderate: 0,
      Low: 0,
      Negligible: 0
    };

    Object.values(riskFormData).forEach((section) => {
      Object.values(section.areaRisks || {}).forEach((risks) => {
        if (Array.isArray(risks)) {
          risks.forEach(risk => {
            if (risk.status === 'Saved' && risk.priority) {
              const normalized = risk.priority.charAt(0).toUpperCase() + risk.priority.slice(1).toLowerCase();
              if (priorityCounts.hasOwnProperty(normalized)) {
                priorityCounts[normalized]++;
              }
            }
          });
        }
      });
    });

    const riskSeverity = calculateRiskSeverityScore(priorityCounts);
    const mitigationQuality = calculateMitigationQualityScore(savedControls, residualRiskData, riskFormData);
    const securityThreat = calculateSecurityThreatLevel(securityState);

    return { riskSeverity, mitigationQuality, securityThreat };
  }, [riskFormData, savedControls, residualRiskData, securityState]);

  // Error handling: Show alert if required props are missing
  if (!modelId || !assessmentUuid) {
    console.error('[AssessmentCanvasV2] CRITICAL: Missing required props!', { modelId, assessmentUuid });
    return (
      <Container fluid px="xl" py="xl">
        <Card withBorder radius="md" p="xl">
          <Alert color="red" title="Configuration Error" icon={<IconAlertCircle size={16} />}>
            Missing required parameters (modelId or assessmentUuid). Cannot load assessment canvas.
            <br />
            <Text size="xs" c="dimmed" mt="xs">
              modelId: {modelId || 'UNDEFINED'} | assessmentUuid: {assessmentUuid || 'UNDEFINED'}
            </Text>
          </Alert>
        </Card>
      </Container>
    );
  }

  const handleOpenEvaluation = ({ riskArea, sourceName }) => {
    const sourceObj = (riskFormData?.[riskArea]?.riskSources || []).find((s) => s?.name === sourceName) || { name: sourceName };
    setEvalDrawer({ opened: true, riskArea, source: sourceObj });
  };

  const handleCloseEvaluation = () => setEvalDrawer({ opened: false, riskArea: null, source: null });

  const handleOpenEditCard = (riskArea) => {
    setEditDrawer({ opened: true, riskArea });
  };

  const handleCloseEditCard = () => setEditDrawer({ opened: false, riskArea: null });

  const handleExpandArea = (areaName) => {
    setExpandedArea(current => current === areaName ? null : areaName);
  };

  // Handle Submit for Review
  const handleSubmitForReview = async () => {
    try {
      console.log('[AssessmentCanvasV2] handleSubmitForReview called:', {
        userIsRiskManager,
        modelId,
        assessmentUuid
      });

      if (userIsRiskManager) {
        // Risk Manager: Activate review mode FIRST, then start review session
        // This prevents the state from being lost when fetchAllAssessments() triggers re-render
        console.log('[AssessmentCanvasV2] Activating review mode BEFORE dispatch...');
        setIsReviewModeActive(true);

        console.log('[AssessmentCanvasV2] Dispatching startReview...');
        const result = await dispatch(startReview({ modelId, assessmentUuid }));
        console.log('[AssessmentCanvasV2] startReview result:', result);
        console.log('[AssessmentCanvasV2] Review mode should now be active');
      } else {
        // Regular User: Just submit for review
        await dispatch(submitForReview(modelId, assessmentUuid));
        console.log('[AssessmentCanvasV2] Assessment submitted for review');
      }
    } catch (error) {
      console.error('[AssessmentCanvasV2] Failed to submit for review:', error);
      // If review start failed, exit review mode
      if (userIsRiskManager) {
        setIsReviewModeActive(false);
      }
    }
  };

  // Handle Exit Review Mode with selective data refetch
  const handleExitReview = async () => {
    console.log('[AssessmentCanvasV2] Exiting review mode');

    // Exit review mode FIRST to prevent re-render issues
    setIsReviewModeActive(false);

    // Then refresh ONLY this assessment's data to get latest review status
    // Using fetchSingleAssessment instead of fetchAllAssessments prevents
    // full list re-render that could disrupt state
    try {
      await dispatch(fetchSingleAssessment(assessmentUuid)).unwrap();
      console.log('[AssessmentCanvasV2] Assessment data refreshed after exit');
    } catch (error) {
      console.error('[AssessmentCanvasV2] Failed to refresh assessment:', error);
      // Don't block - user can still see cached data
    }
  };

  // If review mode is active, render ReviewMode instead
  if (isReviewModeActive) {
    console.log('[AssessmentCanvasV2] Rendering review mode, props:', {
      modelId,
      assessmentUuid,
      assessmentName,
      modelName,
      assessmentType
    });

    // Format assessment type for display
    const formattedAssessmentType = assessmentType
      ? assessmentType.split('-').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')
      : 'Self Assessment';

    return (
      <Container fluid px="xl" py="xl">
        <Grid gutter="md">
          {/* Header aligned with panels below */}
          <Grid.Col span={12}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              {/* Left side - Title and caption */}
              <Group gap="md" align="center" wrap="nowrap">
                <IconClipboardList size={28} color="#1976d2" />
                <Stack gap={4}>
                  <Title order={2} fw={700}>
                    Assessment Review: {assessmentName || 'Assessment'}
                  </Title>
                  <Group gap="xs" align="center">
                    <Text size="sm" c="dimmed">{modelName}</Text>
                    <Badge size="sm" variant="light" color="blue">
                      {formattedAssessmentType}
                    </Badge>
                  </Group>
                </Stack>
              </Group>

              {/* Right side - Exit icon */}
              <Tooltip label="Exit Review Mode">
                <ActionIcon
                  variant="light"
                  color="gray"
                  size="lg"
                  onClick={handleExitReview}
                >
                  <IconDoorExit size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Grid.Col>

          {/* Review Mode Canvas */}
          <Grid.Col span={12}>
            <ReviewMode modelId={modelId} assessmentUuid={assessmentUuid} />
          </Grid.Col>
        </Grid>
      </Container>
    );
  }

  return (
    <Container fluid px="xl" py="xl">
      <Stack gap="lg">
        {/* Enhanced Header */}
        <Stack gap="xs">
          <Group gap="md" align="center" justify="space-between" wrap="nowrap" style={{ width: '100%' }}>
            <Group gap="md" align="center">
              <IconClipboardList size={28} color="#1976d2" />
              <Stack gap={0}>
                <Title order={2} fw={700}>{assessmentName || 'Assessment'}</Title>
                <Text size="sm" c="dimmed">{modelName}</Text>
              </Stack>
            </Group>

            {/* Review Actions - Different for Risk Managers vs Regular Users */}
            {assessmentStatus === 'Completed' && (
              <Group gap="sm">
                {userIsRiskManager ? (
                  // Risk Manager View: Show appropriate badge/button based on review_status
                  (() => {
                    // Review completed (approved)
                    if (reviewStatus === 'approved') {
                      return (
                        <Badge
                          size="lg"
                          variant="light"
                          color="green"
                          leftSection={<IconCheck size={14} />}
                        >
                          Review Approved
                        </Badge>
                      );
                    }

                    // Review completed (rejected)
                    if (reviewStatus === 'rejected') {
                      return (
                        <Badge
                          size="lg"
                          variant="light"
                          color="red"
                          leftSection={<IconX size={14} />}
                        >
                          Review Rejected
                        </Badge>
                      );
                    }

                    // Review in progress (user started review, can resume)
                    if (reviewStatus === 'in_progress') {
                      return (
                        <Button
                          variant="light"
                          color="blue"
                          leftSection={<IconPlayerPlay size={16} />}
                          onClick={handleSubmitForReview}
                          loading={reviewLoading}
                        >
                          Resume Review
                        </Button>
                      );
                    }

                    // Review submitted (waiting for risk manager to start)
                    if (reviewStatus === 'submitted') {
                      return (
                        <Button
                          variant="light"
                          color="orange"
                          leftSection={<IconSend size={16} />}
                          onClick={handleSubmitForReview}
                          loading={reviewLoading}
                        >
                          Start Review
                        </Button>
                      );
                    }

                    // No review yet (null) - show start review
                    return (
                      <Button
                        variant="light"
                        color="orange"
                        leftSection={<IconSend size={16} />}
                        onClick={handleSubmitForReview}
                        loading={reviewLoading}
                      >
                        Start Review
                      </Button>
                    );
                  })()
                ) : (
                  // Regular User View: Request Review button
                  (() => {
                    // Review completed (approved)
                    if (reviewStatus === 'approved') {
                      return (
                        <Badge
                          size="lg"
                          variant="light"
                          color="green"
                          leftSection={<IconCheck size={14} />}
                        >
                          Review Approved
                        </Badge>
                      );
                    }

                    // Review completed (rejected)
                    if (reviewStatus === 'rejected') {
                      return (
                        <Badge
                          size="lg"
                          variant="light"
                          color="red"
                          leftSection={<IconX size={14} />}
                        >
                          Review Rejected
                        </Badge>
                      );
                    }

                    // Review in progress or submitted
                    if (reviewStatus === 'in_progress' || reviewStatus === 'submitted') {
                      return (
                        <Badge
                          size="lg"
                          variant="light"
                          color="orange"
                          leftSection={<IconCheck size={14} />}
                        >
                          In Review
                        </Badge>
                      );
                    }

                    // No review yet (null) - show request review
                    return (
                      <Button
                        variant="light"
                        color="blue"
                        leftSection={<IconSend size={16} />}
                        onClick={handleSubmitForReview}
                        loading={reviewLoading}
                      >
                        Request Review
                      </Button>
                    );
                  })()
                )}
              </Group>
            )}
          </Group>

          {/* Review Session Info */}
          {reviewSessionInfo?.startedBy && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                Review started by {reviewSessionInfo.startedBy}
              </Text>
              {reviewSessionInfo.startedAt && (
                <Text size="sm" c="dimmed">
                  on {new Date(reviewSessionInfo.startedAt).toLocaleDateString()}
                </Text>
              )}
            </Group>
          )}
          {reviewSessionInfo?.reviewedBy && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                Reviewed by {reviewSessionInfo.reviewedBy}
              </Text>
              {reviewSessionInfo.reviewedAt && (
                <Text size="sm" c="dimmed">
                  on {new Date(reviewSessionInfo.reviewedAt).toLocaleDateString()}
                </Text>
              )}
            </Group>
          )}

          <Text size="sm" c="dimmed">
            Safety risks and scenarios overview • {areas.length} risk areas analyzed
          </Text>
        </Stack>

        {/* AI-Powered Score Cards */}
        <Grid gutter="md" justify="center">
          <Grid.Col span={{ base: 12, sm: 4, md: 4 }}>
            <Card
              withBorder
              radius="md"
              p="md"
              style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              onClick={() => setExplanationModal({ opened: true, scoreType: 'riskSeverity', scoreData: aiScores.riskSeverity })}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Stack gap="md" align="center">
                <Group gap="xs">
                  <IconTarget size={20} color="gray" opacity={0.6} />
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500} style={{ letterSpacing: '0.5px' }}>Risk Severity</Text>
                </Group>
                <Badge
                  size="lg"
                  variant="light"
                  color={aiScores.riskSeverity.level === 'HIGH' ? 'red' : aiScores.riskSeverity.level === 'MEDIUM' ? 'orange' : 'green'}
                  styles={{ root: { textTransform: 'capitalize' } }}
                >
                  {aiScores.riskSeverity.level}
                </Badge>
                <Text size="xs" c="dimmed" style={{ opacity: 0.7 }}>
                  {aiScores.riskSeverity.score}/100
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 4 }}>
            <Card
              withBorder
              radius="md"
              p="md"
              style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              onClick={() => setExplanationModal({ opened: true, scoreType: 'mitigationQuality', scoreData: aiScores.mitigationQuality })}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Stack gap="md" align="center">
                <Group gap="xs">
                  <IconShield size={20} color="gray" opacity={0.6} />
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500} style={{ letterSpacing: '0.5px' }}>Mitigation Quality</Text>
                </Group>
                <Badge
                  size="lg"
                  variant="light"
                  color={aiScores.mitigationQuality.level === 'EFFECTIVE' ? 'green' : aiScores.mitigationQuality.level === 'MODERATE' ? 'yellow' : 'red'}
                  styles={{ root: { textTransform: 'capitalize' } }}
                >
                  {aiScores.mitigationQuality.level}
                </Badge>
                <Text size="xs" c="dimmed" style={{ opacity: 0.7 }}>
                  {aiScores.mitigationQuality.score}/100
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 4 }}>
            <Card
              withBorder
              radius="md"
              p="md"
              style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              onClick={() => setExplanationModal({ opened: true, scoreType: 'securityThreat', scoreData: aiScores.securityThreat })}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Stack gap="md" align="center">
                <Group gap="xs">
                  <IconAlertTriangle size={20} color="gray" opacity={0.6} />
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500} style={{ letterSpacing: '0.5px' }}>Security Threat</Text>
                </Group>
                <Badge
                  size="lg"
                  variant="light"
                  color={aiScores.securityThreat.level === 'CRITICAL' ? 'red' : aiScores.securityThreat.level === 'HIGH' ? 'orange' : aiScores.securityThreat.level === 'MEDIUM' ? 'yellow' : 'green'}
                  styles={{ root: { textTransform: 'capitalize' } }}
                >
                  {aiScores.securityThreat.level}
                </Badge>
                <Text size="xs" c="dimmed" style={{ opacity: 0.7 }}>
                  {aiScores.securityThreat.score}/100
                </Text>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        <Tabs defaultValue="overview" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="cards" leftSection={<IconClipboardList size={16} />}>
              Safety & Alignment Risks
            </Tabs.Tab>
            <Tabs.Tab value="security" leftSection={<IconShield size={16} />}>
              Security Threats
            </Tabs.Tab>
            <Tabs.Tab value="actionPlan" leftSection={<IconTarget size={16} />}>
              Action Plan
            </Tabs.Tab>
            <Tabs.Tab value="monitors" leftSection={<IconRadar size={16} />}>
              Monitors
            </Tabs.Tab>
            {userIsRiskManager && (
              <Tabs.Tab value="copilot" leftSection={<IconRobot size={16} />}>
                Co-Pilot
              </Tabs.Tab>
            )}
            {userIsRiskManager && assessmentStatus === 'Completed' && (
              <Tabs.Tab value="toolkit" leftSection={<IconShield size={16} />}>
                Risk Manager Toolkit
              </Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="overview" pt="lg">
            <ChartsSummaryV2 
              riskFormData={riskFormData} 
              residualRiskData={residualRiskData}
              savedControls={savedControls}
              modelId={modelId}
            />
          </Tabs.Panel>

          <Tabs.Panel value="security" pt="lg">
            <SecurityThreatsCardV2
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              userIsRiskManager={userIsRiskManager}
              currentUser={currentUser}
            />
          </Tabs.Panel>

          <Tabs.Panel value="cards" pt="lg">
            <Stack gap="md">
              {/* Instructions for better UX */}
              <Card withBorder radius="md" p="md" bg="blue.0">
                <Text size="sm" c="blue.7">
                  💡 <strong>Tip:</strong> Click on any risk area card to expand and see detailed risks and actions.
                  Cards will expand to full width for better readability.
                </Text>
              </Card>

              {/* Dynamic Grid Layout */}
              {expandedArea ? (
                // When an area is expanded, show it full width with others minimized
                <Stack gap="md">
                  {/* Minimized other areas */}
                  <Grid gutter="md">
                    {areas
                      .filter(areaName => areaName !== expandedArea)
                      .map((areaName) => (
                        <Grid.Col span={6} key={`minimized-${areaName}`}>
                          <SectionAssessmentCardV2
                            modelId={modelId}
                            assessmentUuid={assessmentUuid}
                            areaName={areaName}
                            areaData={riskFormData[areaName]}
                            originalAreaData={originalRiskFormDataRef.current?.[areaName]}
                            residualRiskData={residualRiskData}
                            onEvaluateAction={handleOpenEvaluation}
                            onEditCard={handleOpenEditCard}
                            isExpanded={false}
                            onToggleExpand={handleExpandArea}
                            isMinimized={true}
                          />
                        </Grid.Col>
                      ))}
                  </Grid>

                  {/* Expanded area - full width */}
                  <SectionAssessmentCardV2
                    modelId={modelId}
                    assessmentUuid={assessmentUuid}
                    areaName={expandedArea}
                    areaData={riskFormData[expandedArea]}
                    originalAreaData={originalRiskFormDataRef.current?.[expandedArea]}
                    residualRiskData={residualRiskData}
                    onEvaluateAction={handleOpenEvaluation}
                    onEditCard={handleOpenEditCard}
                    isExpanded={true}
                    onToggleExpand={handleExpandArea}
                    isMinimized={false}
                  />
                </Stack>
              ) : (
                // Default 2-column layout when no area is expanded
                <Grid gutter="lg">
                  {areas.map((areaName) => (
                    <Grid.Col span={{ base: 12, md: 6 }} key={areaName}>
                      <SectionAssessmentCardV2
                        modelId={modelId}
                        assessmentUuid={assessmentUuid}
                        areaName={areaName}
                        areaData={riskFormData[areaName]}
                        originalAreaData={originalRiskFormDataRef.current?.[areaName]}
                        residualRiskData={residualRiskData}
                        onEvaluateAction={handleOpenEvaluation}
                        onEditCard={handleOpenEditCard}
                        isExpanded={false}
                        onToggleExpand={handleExpandArea}
                        isMinimized={false}
                      />
                    </Grid.Col>
                  ))}
                </Grid>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="actionPlan" pt="lg">
            <Card withBorder radius="md" p="lg">
              <ActionPlan modelId={modelId} assessmentUuid={assessmentUuid} riskFormData={riskFormData} />
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="monitors" pt="lg">
            <Card withBorder radius="md" p="lg">
              <Monitors modelId={modelId} />
            </Card>
          </Tabs.Panel>

          {userIsRiskManager && (
            <Tabs.Panel value="copilot" pt="lg">
              <Card withBorder radius="md" p="lg">
                <Stack gap="lg">
                  <Group gap="md" align="center">
                    <IconRobot size={24} />
                    <div>
                      <Text size="lg" fw={600}>Co-Pilot Assessment Mode</Text>
                      <Text size="sm" c="dimmed">
                        Manual risk assessment interface for detailed review and analysis.
                        Use this to assess risks step-by-step with AI assistance.
                      </Text>
                    </div>
                  </Group>

                  {/* Co-Pilot interface for each risk area */}
                  <Stack gap="md">
                    {areas.length > 0 ? (
                      areas.map((areaName) => (
                        <Card key={areaName} withBorder p="md">
                          <Text size="md" fw={600} mb="sm">{areaName.replace(/([a-z])([A-Z])/g, '$1 $2')}</Text>
                          <RiskFlowWrapperV2
                            modelId={modelId}
                            assessmentUuid={assessmentUuid}
                            riskArea={areaName}
                            narratives={narratives}
                            onAreaDone={() => {}}
                          />
                        </Card>
                      ))
                    ) : (
                      <Text c="dimmed">No risk areas available</Text>
                    )}
                  </Stack>
                </Stack>
              </Card>
            </Tabs.Panel>
          )}

          {userIsRiskManager && assessmentStatus === 'Completed' && (
            <Tabs.Panel value="toolkit" pt="lg">
              <RiskManagerToolkit
                modelId={modelId}
                assessmentUuid={assessmentUuid}
                riskFormData={riskFormData}
                narratives={narratives}
              />
            </Tabs.Panel>
          )}
        </Tabs>

        <Drawer
          opened={evalDrawer.opened}
          onClose={handleCloseEvaluation}
          position="right"
          size="xl"
          title="Evaluate Actions"
        >
          {evalDrawer.opened && (
            <ControlEvaluationWizard
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              riskArea={evalDrawer.riskArea}
              source={evalDrawer.source}
            />
          )}
        </Drawer>

        <Drawer
          opened={editDrawer.opened}
          onClose={handleCloseEditCard}
          position="right"
          size="xl"
          title={`Edit Risk Sources & Scenarios - ${editDrawer.riskArea ? editDrawer.riskArea.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, l => l.toUpperCase()) : ''}`}
        >
          {editDrawer.opened && (
            <RiskFlowWrapperV2
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              riskArea={editDrawer.riskArea}
              onAreaDone={handleCloseEditCard}
              narratives={{}}
            />
          )}
        </Drawer>

        <AIScoreExplanationModal
          opened={explanationModal.opened}
          onClose={() => setExplanationModal({ opened: false, scoreType: null, scoreData: null })}
          scoreType={explanationModal.scoreType}
          scoreData={explanationModal.scoreData}
        />
      </Stack>
    </Container>
  );
}